import { TExternalScript, TExternalScriptOptions } from '@kurtosys/types/utils/TExternalScript.js';

export async function loadExternalScripts(
	externalScripts: TExternalScript[],
	onComplete: () => void,
	baseAddress?: string,
): Promise<void> {
	let hasError = false;
	if (externalScripts && externalScripts.length > 0) {
		const promises = externalScripts.map((externalScript) => loadScript(externalScript, baseAddress));
		try {
			await Promise.all(promises);
		} catch (ex) {
			console.log({ ex });
			hasError = true;
		}
	}
	if (!hasError) {
		if (onComplete) {
			onComplete();
		}
	}
}

export async function loadScript(script: TExternalScript, baseAddress?: string): Promise<HTMLScriptElement> {
	let src = typeof script === 'string' ? script : script.src;
	const options: TExternalScriptOptions = (typeof script === 'string' ? {} : script.options) || {};
	const preventBaseAddressPrefix = (typeof script === 'object' && script.preventBaseAddressPrefix) || false;
	if (src && baseAddress && !src.startsWith('http://') && !src.startsWith('https://') && !preventBaseAddressPrefix) {
		const workingBaseAddress = baseAddress.endsWith('/')
			? baseAddress.substr(0, baseAddress.length - 1)
			: baseAddress;
		const workingSrc = src.startsWith('/') ? src.substr(1) : src;
		src = [workingBaseAddress, workingSrc].join('/');
	}

	const scriptStoreKey = 'ksys-loadScript-store';
	if (window && !window[scriptStoreKey]) {
		window[scriptStoreKey] = {};
	}
	const scriptsLoaded = (window && window[scriptStoreKey]) || {};

	for (const documentScript of Array.from(document.scripts)) {
		if (documentScript.dataset.source !== 'ksys') {
			scriptsLoaded[documentScript.src] = true;
		}
	}

	return new Promise((resolve, reject) => {
		let scriptElement: HTMLScriptElement = Array.from(document.scripts).find((script) => script.src === src);
		if (scriptElement && scriptsLoaded[src]) {
			if (typeof script === 'object' && script.children && script.children.length > 0) {
				loadExternalScripts(
					script.children,
					() => {
						resolve(scriptElement);
					},
					baseAddress,
				);
			} else {
				resolve(scriptElement);
			}
		} else {
			let mustInsert = false;
			if (!scriptElement) {
				mustInsert = true;
				scriptElement = document.createElement('script');
				if (options) {
					const optionKeys = Object.keys(options);
					if (optionKeys && optionKeys.length > 0) {
						for (const key of optionKeys) {
							scriptElement[key] = options[key];
						}
					}
				}
				// Do this here to stop someone overriding these options
				scriptElement.dataset.source = 'ksys';
				// Adding cfasync data property to ignore Rocket Loader from cloudflare
				scriptElement.dataset.cfasync = 'false';
				scriptElement.type = 'text/javascript';
				scriptElement.async = false;
				scriptElement.src = src;
				scriptElement.crossOrigin = 'crossorigin';
			}
			if (scriptElement.addEventListener) {
				scriptElement.addEventListener(
					'load',
					() => {
						scriptsLoaded[src] = true;
						if (typeof script === 'object' && script.children && script.children.length > 0) {
							loadExternalScripts(
								script.children,
								() => {
									resolve(scriptElement);
								},
								baseAddress,
							);
						} else {
							resolve(scriptElement);
						}
					},
					false,
				);
				scriptElement.addEventListener(
					'error',
					() => {
						delete scriptsLoaded[src];
						reject(`Could not load script: ${src}`);
					},
					false,
				);
			} else {
				reject('No addEventListener on scriptElement');
			}

			if (mustInsert) {
				const currentScript = getCurrentScript();
				if (currentScript && currentScript.parentNode) {
					currentScript.parentNode.insertBefore(scriptElement, currentScript);
				} else {
					reject('Could not find document.currentScript or document.currentScript.parentNode');
				}
			}
		}
	});
}

export function getCurrentScript(): HTMLOrSVGScriptElement {
	return (
		document.currentScript ||
		((): HTMLScriptElement => {
			const scripts = document.getElementsByTagName('script');
			return scripts[scripts.length - 1];
		})()
	);
}
